import React, {Suspense} from 'react';

import {ErrorBoundary} from '../ErrorBoundary';
import {
  AgentChatAvailabilityContext,
  useComputeAgentChatAvailability,
} from './useAgentChatAvailability';

// Lazy-load the AUI runtime provider to code-split `@assistant-ui/react` and
// runtime dependencies. Only loaded when the feature is fully available.
const AgentChatRuntimeProvider = React.lazy(
  () => import('./AgentChatRuntimeProvider')
);

/**
 * Top-level provider for the Agent Chat feature.
 *
 * When the feature is available (gate + org settings pass), mounts the chat
 * store and lazy-loads the AUI runtime. The runtime gracefully handles missing
 * entity/project context (turns are no-ops until context is present).
 */
export function AgentChatProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const availability = useComputeAgentChatAvailability();

  return (
    <AgentChatAvailabilityContext.Provider value={availability}>
      {availability.isAvailable ? (
        <ErrorBoundary renderError={() => <>{children}</>}>
          <Suspense fallback={<>{children}</>}>
            <AgentChatRuntimeProvider>{children}</AgentChatRuntimeProvider>
          </Suspense>
        </ErrorBoundary>
      ) : (
        <>{children}</>
      )}
    </AgentChatAvailabilityContext.Provider>
  );
}
